import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useMediaQuery,
  useTheme,
  Select,
  InputLabel,
  MenuItem,
  FormControl,
  SelectChangeEvent,
  TextField,
  Tabs,
  Tab,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { useTranslation } from "react-i18next";

const SummaryCard = styled((props) => <Paper elevation={0} {...props} />)(
  ({ theme }) => ({
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2),
    border: `0px solid ${theme.palette.primary.main}`,
  })
);

const SummaryBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(2),
  backgroundColor: "#DBE3FE",
  marginRight: theme.spacing(2),
  borderRadius: theme.spacing(1),
}));

const WhiteBackgroundBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#FFFFFF",
  padding: theme.spacing(4),
  boxShadow: "none",
  width: "100%",
  margin: "0 auto",
  [theme.breakpoints.up("sm")]: {
    width: "83%",
  },
}));

const EVENT_CATEGORIES = [
  "Classical Music",
  "Orchestral Music",
  "Chamber Music",
  "Traditional / Folk Music",
  "Jazz & Contemporary",
  "Rock, Alternative",
  "Electronic Music",
  "Film & Popular Music",
  "Special & Themed Concerts",
  "Masterclasses & Workshops",
  "Youth & Family Concerts",
  "Educational Talks & Lectures",
  "Exhibitions",
  "Other",
];

interface Order {
  orderId: string;
  date: string;
  event: string;
  customerName: string;
  soldTickets: number;
  price: number;
  eventCategory: string;
}

interface VendorOrder {
  orderId: string;
  date: string;
  event: string;
  customerName: string;
  soldTickets: number;
  price: number;
  vendorName: string;
  vendorId: number | null;
}

interface Props {
  vendorOrdersList: any[];
  vendorsList: { vendor_id: number; vendor_name: string }[];
}

// ─── Tab 1: All Orders ───────────────────────────────────────────────────────

const AllOrdersTab: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const { ordersList } = useSelector(
    (state: RootState) => state?.salesManagementReducer
  );

  const ordersData: Order[] =
    ordersList?.map((order: any) => ({
      orderId: order.reserv_id,
      date: order.po_date,
      event: order.eventName_en,
      customerName: order.user_name,
      soldTickets: order.sold_ticket,
      price: parseFloat(order.price ?? order.proce) || 0,
      eventCategory: order.eventCategory,
    })) || [];

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  const filteredOrdersData = ordersData
    .filter((order) => {
      let inRange = true;
      if (fromDate) inRange = new Date(order.date) >= new Date(fromDate);
      if (toDate && inRange) inRange = new Date(order.date) <= new Date(toDate);
      const matchCat = selectedCategory ? order.eventCategory === selectedCategory : true;
      return inRange && matchCat;
    })
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const summaryData = {
    Customer: { value: filteredOrdersData.length },
    Income: { value: filteredOrdersData.reduce((acc, o) => acc + o.price, 0) },
  };

  return (
    <>
      {/* Filters */}
      <Box sx={{ mt: 2, display: "flex", flexDirection: isSmallScreen ? "column" : "row", gap: 2, justifyContent: "flex-end" }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Event Category</InputLabel>
          <Select value={selectedCategory} label="Event Category" onChange={(e: SelectChangeEvent) => { setSelectedCategory(e.target.value); setPage(0); }}>
            <MenuItem value="">All Categories</MenuItem>
            {EVENT_CATEGORIES.map((cat) => (
              <MenuItem key={cat} value={cat}>{cat}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField label="From Date" type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setPage(0); }} InputLabelProps={{ shrink: true }} size="small" />
        <TextField label="To Date" type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setPage(0); }} InputLabelProps={{ shrink: true }} size="small" />
      </Box>

      {/* Summary */}
      <Box mt={3}>
        <Grid container spacing={isSmallScreen ? 2 : 4}>
          <Grid item xs={12} sm={4}>
            <SummaryCard>
              <SummaryBox width={60}><Typography fontWeight="bold" color="#4D67EB">#{summaryData.Customer.value}</Typography></SummaryBox>
              <Box><Typography variant="subtitle2" color="textSecondary">Orders</Typography><Typography variant="h6" fontWeight="bold">{summaryData.Customer.value}</Typography></Box>
            </SummaryCard>
          </Grid>
          <Grid item xs={12} sm={4}>
            <SummaryCard>
              <SummaryBox width={60}><Typography fontWeight="bold" color="#4D67EB">AMD</Typography></SummaryBox>
              <Box><Typography variant="subtitle2" color="textSecondary">Income</Typography><Typography variant="h6" fontWeight="bold">{summaryData.Income.value}</Typography></Box>
            </SummaryCard>
          </Grid>
        </Grid>
      </Box>

      {/* Table */}
      <Box mt={4}>
        <Paper elevation={0}>
          <TableContainer sx={{ maxHeight: 440, overflowX: isSmallScreen ? "auto" : "visible" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ORDER</TableCell>
                  <TableCell>DATE</TableCell>
                  <TableCell>EVENT</TableCell>
                  <TableCell>CATEGORY</TableCell>
                  <TableCell>CUSTOMER</TableCell>
                  <TableCell>TICKETS</TableCell>
                  <TableCell>PRICE</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOrdersData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((order) => (
                  <TableRow key={order.orderId} hover>
                    <TableCell>{order.orderId}</TableCell>
                    <TableCell>{order.date}</TableCell>
                    <TableCell><b>{order.event}</b></TableCell>
                    <TableCell>{order.eventCategory}</TableCell>
                    <TableCell>{order.customerName}</TableCell>
                    <TableCell>{order.soldTickets}</TableCell>
                    <TableCell>{order.price} AMD</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredOrdersData.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </Paper>
      </Box>
    </>
  );
};

// ─── Tab 2: Orders by Vendors ─────────────────────────────────────────────────

const OrdersByVendorsTab: React.FC<{ vendorOrdersList: any[]; vendorsList: { vendor_id: number; vendor_name: string }[] }> = ({ vendorOrdersList, vendorsList }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const vendorOrders: VendorOrder[] = (vendorOrdersList || [])
    .filter((order: any) => order.vendor?.vendor_id != null)
    .map((order: any) => ({
      orderId: order.reserv_id,
      date: order.po_date,
      event: order.eventName_en,
      customerName: order.user_name,
      soldTickets: order.sold_ticket,
      price: parseFloat(order.price),
      vendorName: order.vendor?.vendor_name || "N/A",
      vendorId: order.vendor?.vendor_id || null,
    }));

  const vendorNames = useMemo(() => {
    return vendorsList.map((v) => v.vendor_name).sort((a, b) => a.localeCompare(b));
  }, [vendorsList]);

  const [selectedVendor, setSelectedVendor] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const filtered = vendorOrders
    .filter((o) => {
      if (selectedVendor && o.vendorName !== selectedVendor) return false;
      if (fromDate && new Date(o.date) < new Date(fromDate)) return false;
      if (toDate && new Date(o.date) > new Date(toDate)) return false;
      return true;
    })
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  // Per-vendor summary for the selected set
  const vendorSummary = useMemo(() => {
    const map: Record<string, { count: number; income: number }> = {};
    filtered.forEach((o) => {
      if (!map[o.vendorName]) map[o.vendorName] = { count: 0, income: 0 };
      map[o.vendorName].count += 1;
      map[o.vendorName].income += o.price;
    });
    return map;
  }, [filtered]);

  const totalIncome = filtered.reduce((acc, o) => acc + o.price, 0);

  return (
    <>
      {/* Filters */}
      <Box sx={{ mt: 2, display: "flex", flexDirection: isSmallScreen ? "column" : "row", gap: 2, justifyContent: "flex-end" }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Vendor</InputLabel>
          <Select value={selectedVendor} label="Vendor" onChange={(e: SelectChangeEvent) => { setSelectedVendor(e.target.value); setPage(0); }}>
            <MenuItem value="">All Vendors</MenuItem>
            {vendorNames.map((v) => (
              <MenuItem key={v} value={v}>{v}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField label="From Date" type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setPage(0); }} InputLabelProps={{ shrink: true }} size="small" />
        <TextField label="To Date" type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setPage(0); }} InputLabelProps={{ shrink: true }} size="small" />
      </Box>

      {/* Per-vendor summary chips */}
      <Box mt={3} display="flex" flexWrap="wrap" gap={1}>
        {Object.entries(vendorSummary).map(([name, { count, income }]) => (
          <Chip
            key={name}
            label={`${name} — ${count} orders · ${income} AMD`}
            onClick={() => { setSelectedVendor(selectedVendor === name ? "" : name); setPage(0); }}
            variant={selectedVendor === name ? "filled" : "outlined"}
            sx={{ borderColor: "#4D67EB", color: selectedVendor === name ? "#fff" : "#4D67EB", backgroundColor: selectedVendor === name ? "#4D67EB" : "transparent", cursor: "pointer" }}
          />
        ))}
      </Box>

      {/* Total summary */}
      <Box mt={2}>
        <Grid container spacing={isSmallScreen ? 2 : 4}>
          <Grid item xs={12} sm={4}>
            <SummaryCard>
              <SummaryBox width={60}><Typography fontWeight="bold" color="#4D67EB">#{filtered.length}</Typography></SummaryBox>
              <Box><Typography variant="subtitle2" color="textSecondary">Orders</Typography><Typography variant="h6" fontWeight="bold">{filtered.length}</Typography></Box>
            </SummaryCard>
          </Grid>
          <Grid item xs={12} sm={4}>
            <SummaryCard>
              <SummaryBox width={60}><Typography fontWeight="bold" color="#4D67EB">AMD</Typography></SummaryBox>
              <Box><Typography variant="subtitle2" color="textSecondary">Total Income</Typography><Typography variant="h6" fontWeight="bold">{totalIncome}</Typography></Box>
            </SummaryCard>
          </Grid>
        </Grid>
      </Box>

      {/* Table */}
      <Box mt={4}>
        <Paper elevation={0}>
          <TableContainer sx={{ maxHeight: 440, overflowX: isSmallScreen ? "auto" : "visible" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ORDER</TableCell>
                  <TableCell>DATE</TableCell>
                  <TableCell>EVENT</TableCell>
                  <TableCell>CUSTOMER</TableCell>
                  <TableCell>TICKETS</TableCell>
                  <TableCell>PRICE</TableCell>
                  <TableCell>VENDOR</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((order) => (
                  <TableRow key={order.orderId} hover>
                    <TableCell>{order.orderId}</TableCell>
                    <TableCell>{order.date}</TableCell>
                    <TableCell><b>{order.event}</b></TableCell>
                    <TableCell>{order.customerName}</TableCell>
                    <TableCell>{order.soldTickets}</TableCell>
                    <TableCell>{order.price} AMD</TableCell>
                    <TableCell>
                      <Chip
                        label={order.vendorName}
                        size="small"
                        variant={order.vendorName === "N/A" ? "outlined" : "filled"}
                        sx={{
                          backgroundColor: order.vendorName === "N/A" ? "transparent" : "#DBE3FE",
                          color: order.vendorName === "N/A" ? "#aaa" : "#4D67EB",
                          borderColor: "#ccc",
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </Paper>
      </Box>
    </>
  );
};

// ─── Root component ───────────────────────────────────────────────────────────

const OrderListTable: React.FC<Props> = ({ vendorOrdersList, vendorsList }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Box display="flex" minHeight="90vh" minWidth="100vw" bgcolor="#f0f2f5" padding={isSmallScreen ? 2 : 4}>
      <WhiteBackgroundBox>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold" }}>
          {t("Order List")}
        </Typography>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
          {t("Summary of the order list that you have")}
        </Typography>

        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ borderBottom: 1, borderColor: "divider", mb: 1 }}
        >
          <Tab label="Orders" />
          <Tab label="Orders by Vendors" />
        </Tabs>

        {activeTab === 0 && <AllOrdersTab />}
        {activeTab === 1 && <OrdersByVendorsTab vendorOrdersList={vendorOrdersList} vendorsList={vendorsList} />}
      </WhiteBackgroundBox>
    </Box>
  );
};

export default OrderListTable;
